import { FC, useContext, useEffect, useState } from 'react';
import ProposalModal from '../Create/ProposalNew';
import { useTranslation } from 'react-i18next';
import ProposalList from '../ProposalList';
import { faPencilSlash } from '@fortawesome/sharp-duotone-solid-svg-icons';
import Button from 'components/ui/Button/Button';
import Tabs from 'components/ui/Tabs/Tabs';
import { TripBuilder } from '../TB/TripBuilder';
import { useLocation } from 'react-router-dom';
import TripBuilderProposal from '../TB/TripBuilderProposal';
import { ProposalData } from 'ts/shared/interfaces/ProposalData';
import { hasProposalProducts } from 'utils/shared/proposal';
import { MainContext } from 'contexts/MainContext';
import ProposalAssistantBridge from '../Assistant/ProposalAssistantBridge';

const ProposalPage: FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [createdProposal, setCreatedProposal] = useState<ProposalData>();
  const { proposal, setProposal } = useContext(MainContext);
  const { t } = useTranslation();
  const location = useLocation();
  const isTripBuilderEditMode =
    !!proposal?.existingProposal;

  useEffect(() => {
    const tab = sessionStorage.getItem('proposalActiveTab');
    if (tab) {
      setActiveTab(Number(tab));
      sessionStorage.removeItem('proposalActiveTab');
    }
  }, [location]);

  useEffect(() => {
    const openDetailsTab = () => setActiveTab(0);
    window.addEventListener('proposal:open-details-tab', openDetailsTab);
    return () => {
      window.removeEventListener('proposal:open-details-tab', openDetailsTab);
    };
  }, []);

  const onResetProposal = () => {
    setProposal({ hotels: [], flights: [], dynamic: [], ilPackages: [], transfers: [] });
    setActiveTab(1);
  };

  const onProposalCreated = (proposal: ProposalData) => {
    setCreatedProposal(proposal);
    setActiveTab(1);
  };

  return (
    <div className="pr-page default-shadow">
      <div className="pr-page-nav flex-between" dir="ltr">
        <Tabs
          label={t('proposal.tabs', { defaultValue: 'Proposal' })}
          value={String(activeTab)}
          onValueChange={(value) => setActiveTab(Number(value))}
          items={[
            { value: '1', label: t('search.search') },
            { value: '0', label: t('proposal.item_view') },
            { value: '2', label: t('proposal.trip_view') },
          ]}
        />
        {proposal && hasProposalProducts(proposal) && (
          <Button
            className="btn btn-default pr-source-btn"
            title={t('proposal.reset_proposal', { defaultValue: 'Clear proposal' })}
            icon={faPencilSlash}
            iconclassname="mr-5"
            onClick={onResetProposal}
          />
        )}
      </div>
      <ProposalAssistantBridge setActiveTab={setActiveTab} />
      <div className="pr-page-body" hidden={activeTab !== 0}>
        <ProposalModal onProposalCreated={onProposalCreated} />
      </div>
      {activeTab === 1 && (
        <ProposalList
          onEdit={() => setActiveTab(0)}
          onEditTripBuilder={() => setActiveTab(2)}
          createdProposal={createdProposal}
          onCreatedProposalHandled={() => setCreatedProposal(undefined)}
        />
      )}
      <div className="pr-page-body" hidden={activeTab !== 2}>
        {isTripBuilderEditMode && proposal?.existingProposal ? (
          <div className="pr-tb" dir="ltr">
            <TripBuilderProposal
              proposal={proposal.existingProposal}
              onProposalUpdated={(updatedProposal) =>
                setProposal((prev) => ({ ...prev, existingProposal: updatedProposal }))
              }
              onProposalSaved={(savedProposal) => {
                setProposal((prev) => ({
                  ...prev,
                  hotels: [],
                  flights: [],
                  dynamic: [],
                  ilPackages: [],
                  transfers: [],
                  existingProposal: savedProposal,
                }));
              }}
            />
            <div className="pr-tb-side">
              <ProposalModal
                onProposalCreated={onProposalCreated}
                listenToAssistant={false}
                tripBuilderMode
              />
            </div>
          </div>
        ) : (
          <TripBuilder onProposalCreated={onProposalCreated} />
        )}
      </div>
    </div>

  );
};

export default ProposalPage;
